<template>
  <f7-sheet
    class="supplier-invoice-sheet"
    :opened="opened"
    backdrop
    close-by-backdrop-click
    @sheet:closed="onClosed"
  >
    <f7-toolbar>
      <div class="left" />
      <div class="center">{{ $t('supplier.dailyOrders.invoice.sheetTitle') }}</div>
      <div class="right">
        <f7-link
          sheet-close
          class="supplier-invoice-sheet__close"
          :aria-label="$t('supplier.dailyOrders.sheet.closeAria')"
        >
          <f7-icon f7="xmark" size="20"></f7-icon>
        </f7-link>
      </div>
    </f7-toolbar>

    <f7-page-content>
      <div v-if="loading" class="supplier-invoice-sheet__loading">
        <f7-preloader></f7-preloader>
      </div>

      <template v-else-if="invoice">
        <div class="supplier-invoice-sheet__rows">
          <div class="supplier-invoice-sheet__row">
            <span class="supplier-invoice-sheet__k">
              {{ $t('supplier.dailyOrders.invoice.numberLabel') }}
            </span>
            <span class="supplier-invoice-sheet__v">{{ invoiceNumber }}</span>
          </div>
          <div class="supplier-invoice-sheet__row">
            <span class="supplier-invoice-sheet__k">
              {{ $t('supplier.dailyOrders.invoice.dateLabel') }}
            </span>
            <span class="supplier-invoice-sheet__v">{{ invoiceDateFormatted }}</span>
          </div>
          <div class="supplier-invoice-sheet__row supplier-invoice-sheet__row--total">
            <span class="supplier-invoice-sheet__k">
              {{ $t('supplier.dailyOrders.invoice.totalLabel') }}
            </span>
            <span class="supplier-invoice-sheet__v">{{ totalFormatted }}</span>
          </div>
        </div>

        <button
          v-if="hasDocumentUrl"
          type="button"
          class="supplier-invoice-sheet__open"
          @click="openDocument"
        >
          <f7-icon f7="arrow_up_right_square_fill" size="18"></f7-icon>
          {{ $t('supplier.dailyOrders.invoice.open') }}
        </button>
        <p v-else class="supplier-invoice-sheet__not-ready">
          {{ $t('supplier.dailyOrders.invoice.notReady') }}
        </p>
      </template>

      <p v-else class="supplier-invoice-sheet__not-ready">
        {{ $t('supplier.dailyOrders.invoice.notReady') }}
      </p>
    </f7-page-content>
  </f7-sheet>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency.js';
import { fetchInvoiceForBox } from '@/services/supplierBoxInvoice.service.js';

const props = defineProps({
  // The box whose invoice this sheet shows. Deliberately `boxId`, not an
  // invoice id: the box index never carries the invoice's own id (see
  // supplierBoxInvoice.service.js's docblock), so boxId is the only value
  // both the list card and the detail page reliably hold.
  boxId: {
    type: [String, Number],
    default: null,
  },
  // v-model:opened — the host page owns which box is loaded + drives this flag.
  opened: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:opened']);

const { locale } = useI18n();
const { formatCurrency } = useCurrency();

const invoice = ref(null);
const loading = ref(false);

// `documentUrl` is a 5-minute signed URL (invoice-placement design Section 2.1), so
// EVERY open re-fetches — the box index never carries any invoice field at
// all (not even invoiceNumber/date/total, only `hasInvoice`), so unlike the
// original design's assumption there is no list-cached data to render while
// the fetch is in flight; the whole sheet body is a loading state until it
// resolves, not just the primary action.
async function load() {
  if (props.boxId == null) return;
  loading.value = true;
  invoice.value = null;
  try {
    invoice.value = await fetchInvoiceForBox(props.boxId);
  } catch {
    invoice.value = null;
  } finally {
    loading.value = false;
  }
}

function reset() {
  invoice.value = null;
  loading.value = false;
}

watch(
  () => props.opened,
  (isOpen) => {
    if (isOpen) load();
  },
  { immediate: true },
);

// The sheet is a persistent, page-level singleton reused across many
// open/close cycles (one per box tapped) — reset on EVERY close so a stale
// invoice never flashes before the next box's fresh fetch resolves.
function onClosed() {
  reset();
  if (props.opened) emit('update:opened', false);
}

const hasDocumentUrl = computed(() => Boolean(invoice.value?.documentUrl));
const invoiceNumber = computed(() => invoice.value?.invoiceNumber ?? '—');
const totalFormatted = computed(() => formatCurrency(Number(invoice.value?.totalCents) || 0));

const invoiceDateFormatted = computed(() => {
  const raw = invoice.value?.invoiceDate;
  if (!raw) return '—';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  // `-u-nu-latn` forces Western digits regardless of the active Arabic
  // locale (house rule — see TransactionDetailSheet.vue's identical trick).
  const dateLocale = locale.value === 'ar' ? 'ar-EG-u-nu-latn' : 'en-US';
  return parsed.toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
});

function openDocument() {
  if (!hasDocumentUrl.value) return;
  window.open(invoice.value.documentUrl, '_blank', 'noopener');
}
</script>

<style scoped>
.supplier-invoice-sheet {
  height: 85%;
}

.supplier-invoice-sheet .center {
  font-weight: var(--poolit-font-weight-semibold, 600);
}

.supplier-invoice-sheet__close {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
}

.supplier-invoice-sheet__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
}

.supplier-invoice-sheet__rows {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 4px 16px 18px;
}

.supplier-invoice-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 2px;
  border-bottom: 1px solid var(--poolit-border-color, #f0eef5);
}

.supplier-invoice-sheet__row:last-child {
  border-bottom: none;
}

.supplier-invoice-sheet__k {
  font-size: 13px;
  font-weight: var(--poolit-font-weight-medium, 500);
  color: var(--poolit-text-muted, #6b7280);
}

.supplier-invoice-sheet__v {
  font-size: 14px;
  font-weight: var(--poolit-font-weight-semibold, 600);
  color: var(--f7-text-color);
  font-variant-numeric: tabular-nums;
}

.supplier-invoice-sheet__row--total .supplier-invoice-sheet__v {
  font-size: 16px;
  color: var(--poolit-primary-900, #31006f);
}

.supplier-invoice-sheet__open {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: calc(100% - 32px);
  margin: 0 16px 20px;
  min-height: 50px;
  border: 0;
  border-radius: var(--poolit-radius-lg, 14px);
  background: var(--poolit-primary-700, #6621e2);
  color: #fff;
  font-family: inherit;
  font-size: 15px;
  font-weight: var(--poolit-font-weight-semibold, 600);
  cursor: pointer;
}

.supplier-invoice-sheet__not-ready {
  margin: 4px 16px 20px;
  padding: 12px;
  border-radius: var(--poolit-radius-md, 12px);
  background: var(--poolit-bg-tertiary, #f1f3f5);
  color: var(--poolit-text-muted, #6b7280);
  font-size: 12.5px;
  text-align: center;
  line-height: 1.5;
}
</style>
